import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, Edit, Trash2, Package } from 'lucide-react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';

const Inventory = ({ user }) => {
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [restockItem, setRestockItem] = useState(null);
  const [restockData, setRestockData] = useState({ packs: 0, loosePairs: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [articleFilter, setArticleFilter] = useState('');
  const [colorFilter, setColorFilter] = useState('');
  const [materialFilter, setMaterialFilter] = useState('');
  const [heelFilter, setHeelFilter] = useState('');
  const [inventory, setInventory] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(true);

  const [newItem, setNewItem] = useState({
    articleNumber: '',
    colour: '',
    material: '',
    heel: '',
    pricePerPair: '',
    pairsPerPack: 8,
    packsInStock: 1,
    loosePairs: 0
  });

  useEffect(() => {
    loadInventory();
  }, []);

  const loadInventory = async () => {
    try {
      const [inventorySnapshot, ordersSnapshot] = await Promise.all([
        getDocs(collection(db, 'inventory')),
        getDocs(collection(db, 'orders'))
      ]);
      
      const items = inventorySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const ordersData = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setInventory(items);
      setOrders(ordersData);
    } catch (error) {
      console.error('Error loading inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    try {
      const item = {
        ...newItem,
        pricePerPair: parseFloat(newItem.pricePerPair),
        pairsPerPack: parseInt(newItem.pairsPerPack),
        packsInStock: parseInt(newItem.packsInStock),
        loosePairs: parseInt(newItem.loosePairs) || 0,
        createdAt: new Date().toISOString(),
        createdBy: user.username || user.email
      };
      
      await addDoc(collection(db, 'inventory'), item);
      await loadInventory();
      
      setNewItem({
        articleNumber: '',
        colour: '',
        material: '',
        heel: '',
        pricePerPair: '',
        pairsPerPack: 8,
        packsInStock: 1,
        loosePairs: 0
      });
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item.id);
    setEditData({ ...item });
    setShowAddModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      const updatedData = {
        ...editData,
        pricePerPair: parseFloat(editData.pricePerPair),
        pairsPerPack: parseInt(editData.pairsPerPack),
        packsInStock: parseInt(editData.packsInStock),
        loosePairs: parseInt(editData.loosePairs) || 0,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(doc(db, 'inventory', editingItem), updatedData);
      await loadInventory();
      
      setEditingItem(null);
      setEditData({});
      setShowAddModal(false);
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditData({});
    setShowAddModal(false);
  };

  const handleRestock = async (e) => {
    e.preventDefault();
    try {
      const newPacks = parseInt(restockData.packs) || 0;
      const newLoosePairs = parseInt(restockData.loosePairs) || 0;
      
      if (newPacks === 0 && newLoosePairs === 0) {
        alert('Please enter valid restock quantities.');
        return;
      }
      
      const updatedPacks = restockItem.packsInStock + newPacks;
      const updatedLoosePairs = (restockItem.loosePairs || 0) + newLoosePairs;
      
      await updateDoc(doc(db, 'inventory', restockItem.id), {
        packsInStock: updatedPacks,
        loosePairs: updatedLoosePairs,
        lastRestocked: new Date().toISOString()
      });
      
      // Record restock transaction
      await addDoc(collection(db, 'restocks'), {
        itemId: restockItem.id,
        articleNumber: restockItem.articleNumber,
        colour: restockItem.colour,
        packsAdded: newPacks,
        loosePairsAdded: newLoosePairs,
        totalPairsAdded: (newPacks * restockItem.pairsPerPack) + newLoosePairs,
        restockedBy: user.username || user.email,
        restockedAt: new Date().toISOString()
      });
      
      await loadInventory();
      setShowRestockModal(false);
      setRestockItem(null);
      setRestockData({ packs: 0, loosePairs: 0 });
      
      alert('Item restocked successfully!');
    } catch (error) {
      console.error('Error restocking item:', error);
    }
  };

  const handleDelete = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteDoc(doc(db, 'inventory', itemId));
        await loadInventory();
      } catch (error) {
        console.error('Error deleting item:', error);
      }
    }
  };

  const filteredInventory = inventory.filter(item =>
    (item.articleNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.colour?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.material?.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (articleFilter === '' || item.articleNumber === articleFilter) &&
    (colorFilter === '' || item.colour === colorFilter) &&
    (materialFilter === '' || item.material === materialFilter) &&
    (heelFilter === '' || item.heel === heelFilter)
  );

  return (
    <div className="inventory-page">
      <div className="inventory-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft size={16} style={{ marginRight: '8px' }} />
            Back
          </button>
          <h1 className="inventory-title">Inventory Management</h1>
        </div>
        
        <div className="inventory-actions">
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              <Search 
                size={16} 
                style={{ 
                  position: 'absolute', 
                  left: '15px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: '#666'
                }} 
              />
              <input
                type="text"
                placeholder="Search inventory..."
                className="search-bar"
                style={{ paddingLeft: '45px', minWidth: '200px' }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              value={articleFilter}
              onChange={(e) => setArticleFilter(e.target.value)}
              style={{ padding: '8px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', minWidth: '120px' }}
            >
              <option value="">All Articles</option>
              {[...new Set(inventory.map(item => item.articleNumber).filter(Boolean))].sort().map(article => (
                <option key={article} value={article}>{article}</option>
              ))}
            </select>
            <select
              value={colorFilter}
              onChange={(e) => setColorFilter(e.target.value)}
              style={{ padding: '8px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', minWidth: '120px' }}
            >
              <option value="">All Colors</option>
              {[...new Set(inventory.map(item => item.colour).filter(Boolean))].sort().map(color => (
                <option key={color} value={color}>{color}</option>
              ))}
            </select>
            <select
              value={materialFilter}
              onChange={(e) => setMaterialFilter(e.target.value)}
              style={{ padding: '8px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', minWidth: '120px' }}
            >
              <option value="">All Materials</option>
              {[...new Set(inventory.map(item => item.material).filter(Boolean))].sort().map(material => (
                <option key={material} value={material}>{material}</option>
              ))}
            </select>
            <select
              value={heelFilter}
              onChange={(e) => setHeelFilter(e.target.value)}
              style={{ padding: '8px 12px', border: '2px solid #e1e5e9', borderRadius: '8px', minWidth: '120px' }}
            >
              <option value="">All Heels</option>
              {[...new Set(inventory.map(item => item.heel).filter(Boolean))].sort().map(heel => (
                <option key={heel} value={heel}>{heel}</option>
              ))}
            </select>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={16} style={{ marginRight: '8px' }} />
            Add Item
          </button>
        </div>
      </div>

      <div className="inventory-content">
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: 'white',
            borderRadius: '20px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ color: '#666', marginBottom: '10px' }}>Loading...</h3>
          </div>
        ) : filteredInventory.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: 'white',
            borderRadius: '20px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ color: '#666', marginBottom: '10px' }}>No Items Found</h3>
            <p style={{ color: '#999' }}>Add your first inventory item to get started.</p>
          </div>
        ) : (
          <div className="inventory-grid">
            {filteredInventory.map(item => {
              // Calculate reserved quantity for this item
              let reservedPairs = 0;
              orders.forEach(order => {
                if (order.status !== 'completed' && order.items) {
                  order.items.forEach(orderItem => {
                    if (orderItem.articleNumber === item.articleNumber) {
                      reservedPairs += orderItem.totalPairs || 0;
                    }
                  });
                }
              });
              
              const totalPairs = (item.packsInStock * item.pairsPerPack) + (item.loosePairs || 0);
              const availablePairs = totalPairs - reservedPairs;
              
              return (
              <div key={item.id} className="inventory-item">
                <div className="item-header">
                  <div className="item-article">{item.articleNumber}</div>
                  <div className="item-stock">
                    {item.packsInStock} packs + {item.loosePairs || 0} pairs
                    <br />
                    <small>({totalPairs} total pairs)</small>
                    {reservedPairs > 0 && (
                      <>
                        <br />
                        <small style={{ color: '#f39c12' }}>Reserved: {reservedPairs} pairs</small>
                        <br />
                        <small style={{ color: availablePairs > 0 ? '#27ae60' : '#e74c3c' }}>Available: {availablePairs} pairs</small>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="item-details">
                  <div className="item-detail">
                    <span className="item-detail-label">Colour:</span>
                    <span className="item-detail-value">{item.colour}</span>
                  </div>
                  <div className="item-detail">
                    <span className="item-detail-label">Material:</span>
                    <span className="item-detail-value">{item.material}</span>
                  </div>
                  <div className="item-detail">
                    <span className="item-detail-label">Heel:</span>
                    <span className="item-detail-value">{item.heel}</span>
                  </div>
                  <div className="item-detail">
                    <span className="item-detail-label">Pairs per Pack:</span>
                    <span className="item-detail-value">{item.pairsPerPack}</span>
                  </div>
                </div>
                
                <div className="item-price">
                  LKR {item.pricePerPair?.toLocaleString()} per pair
                  <br />
                  <small style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                    LKR {(item.pricePerPair * item.pairsPerPack)?.toLocaleString()} per pack
                  </small>
                </div>
                
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '10px',
                  marginTop: '15px',
                  paddingTop: '15px',
                  borderTop: '1px solid #eee'
                }}>
                  <button
                    onClick={() => handleEdit(item)}
                    style={{
                      background: '#667eea',
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    <Edit size={14} /> Edit
                  </button>
                  <button
                    onClick={() => {
                      setRestockItem(item);
                      setShowRestockModal(true);
                    }}
                    style={{
                      background: '#27ae60',
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    <Package size={14} /> Restock
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    style={{
                      background: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">{editingItem ? 'Edit Item' : 'Add New Item'}</h2>
            <form onSubmit={editingItem ? handleSaveEdit : handleAddItem} className="modal-form">
              <div className="form-group">
                <label>Article Number</label>
                <input
                  type="text"
                  value={editingItem ? editData.articleNumber : newItem.articleNumber}
                  onChange={(e) => editingItem ? 
                    setEditData({...editData, articleNumber: e.target.value}) :
                    setNewItem({...newItem, articleNumber: e.target.value})
                  }
                  placeholder="e.g., AD003"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Colour</label>
                <input
                  type="text"
                  value={editingItem ? editData.colour : newItem.colour}
                  onChange={(e) => editingItem ? 
                    setEditData({...editData, colour: e.target.value}) :
                    setNewItem({...newItem, colour: e.target.value})
                  }
                  placeholder="e.g., Black, Brown, Tan"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Material</label>
                <input
                  type="text"
                  value={editingItem ? editData.material : newItem.material}
                  onChange={(e) => editingItem ? 
                    setEditData({...editData, material: e.target.value}) :
                    setNewItem({...newItem, material: e.target.value})
                  }
                  placeholder="e.g., Leather, Suede, Canvas"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Heel Type</label>
                <input
                  type="text"
                  value={editingItem ? editData.heel : newItem.heel}
                  onChange={(e) => editingItem ? 
                    setEditData({...editData, heel: e.target.value}) :
                    setNewItem({...newItem, heel: e.target.value})
                  }
                  placeholder="e.g., Flat, Low, High"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Price per Pair (LKR)</label>
                <input
                  type="number"
                  value={editingItem ? editData.pricePerPair : newItem.pricePerPair}
                  onChange={(e) => editingItem ? 
                    setEditData({...editData, pricePerPair: e.target.value}) :
                    setNewItem({...newItem, pricePerPair: e.target.value})
                  }
                  placeholder="3000"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Pairs per Pack</label>
                <input
                  type="number"
                  value={editingItem ? editData.pairsPerPack : newItem.pairsPerPack}
                  onChange={(e) => editingItem ? 
                    setEditData({...editData, pairsPerPack: e.target.value}) :
                    setNewItem({...newItem, pairsPerPack: e.target.value})
                  }
                  placeholder="8"
                  min="1"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Packs in Stock</label>
                <input
                  type="number"
                  value={editingItem ? editData.packsInStock : newItem.packsInStock}
                  onChange={(e) => editingItem ? 
                    setEditData({...editData, packsInStock: e.target.value}) :
                    setNewItem({...newItem, packsInStock: e.target.value})
                  }
                  placeholder="1"
                  min="0"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Loose Pairs</label>
                <input
                  type="number"
                  value={editingItem ? (editData.loosePairs || 0) : (newItem.loosePairs || 0)}
                  onChange={(e) => editingItem ? 
                    setEditData({...editData, loosePairs: e.target.value}) :
                    setNewItem({...newItem, loosePairs: e.target.value})
                  }
                  placeholder="0"
                  min="0"
                />
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={editingItem ? handleCancelEdit : () => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingItem ? 'Update Item' : 'Add Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {showRestockModal && restockItem && (
        <div className="modal-overlay" onClick={() => setShowRestockModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Restock Item</h2>
            <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 10px 0' }}>{restockItem.articleNumber} - {restockItem.colour}</h4>
              <p style={{ margin: 0, color: '#666' }}>Current Stock: {restockItem.packsInStock} packs + {restockItem.loosePairs || 0} pairs</p>
            </div>
            <form onSubmit={handleRestock} className="modal-form">
              <div className="form-group">
                <label>Add Packs</label>
                <input
                  type="number"
                  value={restockData.packs}
                  onChange={(e) => setRestockData({...restockData, packs: e.target.value})}
                  min="0"
                  placeholder="Number of packs to add"
                />
              </div>
              <div className="form-group">
                <label>Add Loose Pairs</label>
                <input
                  type="number"
                  value={restockData.loosePairs}
                  onChange={(e) => setRestockData({...restockData, loosePairs: e.target.value})}
                  min="0"
                  placeholder="Number of loose pairs to add"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowRestockModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Restock Item</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;